/**
 * Глобальный контекст AI-панели.
 *
 * Позволяет открывать/закрывать AI-чат из любого места приложения
 * и передавать текущий контекст (viewId, spaceName, viewName).
 */

import { createContext, useCallback, useContext, useState, type PropsWithChildren } from 'react';

interface AiPanelScope {
  viewId?: string;
  spaceName?: string;
  viewName?: string;
}

interface AiPanelContextValue {
  open: boolean;
  scope: AiPanelScope;
  openPanel: (scope?: AiPanelScope) => void;
  closePanel: () => void;
  togglePanel: (scope?: AiPanelScope) => void;
  setScope: (scope: AiPanelScope) => void;
}

const AiPanelCtx = createContext<AiPanelContextValue | null>(null);

export function AiPanelProvider({ children }: PropsWithChildren): JSX.Element {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<AiPanelScope>({});

  const openPanel = useCallback((s?: AiPanelScope) => {
    if (s) setScope(s);
    setOpen(true);
  }, []);

  const closePanel = useCallback(() => setOpen(false), []);

  const togglePanel = useCallback((s?: AiPanelScope) => {
    setOpen((prev) => {
      if (!prev && s) setScope(s);
      return !prev;
    });
  }, []);

  return (
    <AiPanelCtx.Provider value={{ open, scope, openPanel, closePanel, togglePanel, setScope }}>
      {children}
    </AiPanelCtx.Provider>
  );
}

export function useAiPanel(): AiPanelContextValue {
  const ctx = useContext(AiPanelCtx);
  if (!ctx) throw new Error('useAiPanel must be used within AiPanelProvider');
  return ctx;
}
